import { maxBy, noop, range, sum } from 'lodash';
import { getFramework } from '@/framework';
import { COL_ELEMENT_IS_BLOCK } from '@/utils/common-kit';
import { getLayerViewNode } from '@/utils/layer-kit';
import {
    BaseCells,
    Cell,
    CellStyle,
    DEFAULT_FONT_FAMILY,
    ITableLayer,
    ITableLayerProps,
    LayerType,
    MIN_FONT_SIZE,
} from '@/version-types';
import {
    DEFAULT_TABLE_CELL__TEXT_FONT_SIZE,
    MIN_BASE_CELL_HEIGHT,
    MIN_BASE_CELL_WIDTH,
    MIN_BORDER_WIDTH,
    TABLE_CELL_HORIZONTAL_PADDING,
} from '../default-configs';
import tablePresets from '../presets';
import { getCellComponentHTML, getTdElementId } from '../view/cell-component';
import { emptyBorder } from './cell';

export const hoverAreaClassName = 'hoverArea';

type OverwriteStyle = Pick<CellStyle, 'fontFamily' | 'fontSize' | 'disableAutoWrap'>;

interface GetUpdatedTableStyle_ActionHelp {
    getTdEl: (cellId: string) => HTMLTableCellElement | null;
    /** 修改单个单元格的文字内容或属性 */
    updateTextContentOrAttr: (args: {
        currentTableLayerData: ITableLayerProps;
        fontFamilyMap: Record<string, string>;
        cellInfo: { id: string; hIndex: number; vIndex: number };
        /** 传入该参数代表单元格宽度可坍塌，并最多坍塌到该宽度 (自动换行的单元格才生效) */
        shrinkToCellWidth?: number;
        newContent?: string;
        overwriteStyle?: OverwriteStyle | ((cellId: string) => OverwriteStyle);
    }) => { delta: number };
    /** 修改多个单元格的样式 */
    updateCellStyle: (args: {
        currentTableLayerData: ITableLayerProps;
        fontFamilyMap: Record<string, string>;
        cellInfoList: { id: string; hIndex: number; vIndex: number }[];
        overwriteStyle: OverwriteStyle | ((cellId: string) => OverwriteStyle);
    }) => void;
    scaleFontSizeAndBorderWidth: (args: { currentTableLayerData: ITableLayerProps; scale: number }) => void;
}

interface HeightInfo {
    tableHeight: number;
    verticalSizes: number[];
}

type OldLayoutInfo =
    | {
          tableWidth: number;
          horizontalSizes: number[];
      }
    | {
          tableHeight: number;
          verticalSizes: number[];
      }
    | {
          tableWidth: number;
          horizontalSizes: number[];
          tableHeight: number;
          verticalSizes: number[];
      };

interface LayoutInfo {
    tableWidth: number;
    tableHeight: number;
    baseCells: {
        horizontal: number[];
        vertical: number[];
    };
}

interface GetUpdatedTableStyle_ComputedHelp {
    /**
     * @param oldHeightInfo 用于对比, 如果与旧的数值相近, 则返回旧值, 防止精度问题产生的抖动
     */
    getHeightInfo: (oldHeightInfo?: HeightInfo) => HeightInfo;
    /**
     * @param oldLayout 用于对比, 如果与旧的数值相近, 则返回旧值, 防止精度问题产生的抖动
     */
    getLayout: (oldLayout?: OldLayoutInfo) => LayoutInfo;
}

export type GetUpdatedTableStyle_Action = (el: HTMLTableElement, actionHelp: GetUpdatedTableStyle_ActionHelp) => void;

/** 获取对table元素操作完后的样式信息 */
export function getUpdatedTableStyle<T>(args: {
    frameworkIdentity: string;
    tableLayerId: string;
    tableRotate: number;
    action: GetUpdatedTableStyle_Action;
    compute: (el: HTMLTableElement, computedHelp: GetUpdatedTableStyle_ComputedHelp) => T;
    /** 保留临时element(需要记得手动销毁), 在高度频繁调用该函数时可启用, 以提升性能 */
    preserveTempElement?: boolean;
    /** 方便本地开发, 直接将tempTableEl展示出来 */
    debug?: boolean;
}): T | null;
export function getUpdatedTableStyle<T>(args: {
    tableEl: HTMLTableElement;
    stageScale: number;
    tableRotate: number;
    action: GetUpdatedTableStyle_Action;
    compute: (el: HTMLTableElement, computedHelp: GetUpdatedTableStyle_ComputedHelp) => T;
    /** 保留临时element(需要记得手动销毁), 在高度频繁调用该函数时可启用, 以提升性能 */
    preserveTempElement?: boolean;
    /** 方便本地开发, 直接将tempTableEl展示出来 */
    debug?: boolean;
}): T | null;
export function getUpdatedTableStyle(args) {
    let stageScale: number;
    let tableEl: HTMLTableElement | null;
    if (args.frameworkIdentity) {
        stageScale = getFramework(args.frameworkIdentity).stage.getScale();
        tableEl = getLayerViewNode(args.frameworkIdentity, args.tableLayerId)?.querySelector('table') ?? null;
    } else {
        stageScale = args.stageScale;
        tableEl = args.tableEl;
    }
    if (!tableEl) return null;

    const { tableRotate, action, compute, preserveTempElement } = args as {
        tableRotate: number;
        action: GetUpdatedTableStyle_Action;
        compute: (el: HTMLTableElement, computedHelp: GetUpdatedTableStyle_ComputedHelp) => unknown;
        preserveTempElement?: boolean;
    };

    const getTdEl: GetUpdatedTableStyle_ActionHelp['getTdEl'] = (cellId) => {
        const tdEl = tempEl.querySelector<HTMLTableCellElement>(`td[data-table-cell-id="${cellId}"]`);
        return tdEl;
    };

    const updateTextContentOrAttr: GetUpdatedTableStyle_ActionHelp['updateTextContentOrAttr'] = ({
        currentTableLayerData,
        fontFamilyMap,
        cellInfo,
        shrinkToCellWidth,
        newContent,
        overwriteStyle,
    }) => {
        const cell = currentTableLayerData.cells[cellInfo?.vIndex]?.[cellInfo?.hIndex]!;
        if (!cell) return { delta: 0 };
        let tdEl = getTdEl(cellInfo.id)!;
        if (tdEl)
            tdEl.outerHTML = getCellComponentHTML({
                cellComponentProps: {
                    tableLayerData: currentTableLayerData,
                    fontFamilyMap,
                    cell,
                    isInEditorLayer: true,
                },
                newContent,
                overwriteStyle: typeof overwriteStyle === 'function' ? overwriteStyle(cellInfo.id) : overwriteStyle,
            });

        // 单个字放不下时, 需要撑开单元格宽度
        let newCellWidth: number | null = null;
        const { disableAutoWrap } = getCellStyle(cell, currentTableLayerData);
        if (!disableAutoWrap) {
            tdEl = getTdEl(cellInfo.id)!;
            const textEl = tdEl.querySelector<HTMLSpanElement>('.cell-text');
            const _minCellWidth = Math.max(
                shrinkToCellWidth ?? 0,
                MIN_BASE_CELL_WIDTH,
                Math.ceil((textEl?.getBoundingClientRect().width ?? 0) / stageScale) +
                    TABLE_CELL_HORIZONTAL_PADDING * 2,
            );
            if (
                typeof shrinkToCellWidth === 'number' ||
                Math.round(tdEl.getBoundingClientRect().width / stageScale) < _minCellWidth
            ) {
                newCellWidth = _minCellWidth;
            }
        }
        if (newCellWidth) {
            const oldCellWidth = Math.round(tdEl.getBoundingClientRect().width / stageScale);
            const delta = newCellWidth - oldCellWidth;

            tempEl.style.width = `${parseInt(tempEl.style.width) + delta}px`;

            const colEl = tempEl.querySelector<HTMLTableColElement>(`col:nth-child(${cell.hIndex + 1})`)!;
            colEl.style.width = `${parseInt(colEl.style.width) + delta}px`;

            return {
                delta,
            };
        }

        return {
            delta: 0,
        };
    };

    const updateCellStyle: GetUpdatedTableStyle_ActionHelp['updateCellStyle'] = ({
        currentTableLayerData,
        fontFamilyMap,
        cellInfoList,
        overwriteStyle,
    }) => {
        cellInfoList.forEach((cellInfo) => {
            const cell = currentTableLayerData.cells[cellInfo?.vIndex]?.[cellInfo?.hIndex]!;
            if (!cell) return;
            const tdEl = getTdEl(cellInfo.id)!;
            if (tdEl)
                tdEl.outerHTML = getCellComponentHTML({
                    cellComponentProps: {
                        tableLayerData: currentTableLayerData,
                        fontFamilyMap,
                        cell,
                        isInEditorLayer: true,
                    },
                    overwriteStyle: typeof overwriteStyle === 'function' ? overwriteStyle(cellInfo.id) : overwriteStyle,
                });
        });
    };

    const scaleFontSizeAndBorderWidth: GetUpdatedTableStyle_ActionHelp['scaleFontSizeAndBorderWidth'] = ({
        currentTableLayerData,
        scale,
    }) => {
        updateCellStyle({
            currentTableLayerData,
            fontFamilyMap: {},
            cellInfoList: currentTableLayerData.cells
                .flat()
                .filter(Boolean)
                .map((cell) => ({
                    id: cell?.__renderId!,
                    hIndex: cell?.hIndex!,
                    vIndex: cell?.vIndex!,
                })),
            overwriteStyle: (cellId) => {
                const style = getCellStyle(
                    currentTableLayerData.cells.flat().find((cell) => cell?.__renderId === cellId)!,
                    currentTableLayerData,
                );
                const fontSize = style.fontSize ?? DEFAULT_TABLE_CELL__TEXT_FONT_SIZE;
                const border = {
                    ...emptyBorder,
                    ...style.border,
                };
                return {
                    fontSize: Math.max(MIN_FONT_SIZE, Math.round(fontSize * scale)),
                    border: {
                        ...border,
                        width: Math.max(MIN_BORDER_WIDTH, Math.round(border.width * scale)),
                    },
                };
            },
        });
    };

    const actionHelp: GetUpdatedTableStyle_ActionHelp = {
        getTdEl,
        updateTextContentOrAttr,
        updateCellStyle,
        scaleFontSizeAndBorderWidth,
    };

    const isEqualNumber = (a: number, b: number) => Math.abs(a - b) < 0.5;

    const getHeightInfo: GetUpdatedTableStyle_ComputedHelp['getHeightInfo'] = (oldHeightInfo) => {
        const expectHeightInfo: HeightInfo = {
            tableHeight: tempEl.getBoundingClientRect().height / stageScale,
            verticalSizes: Array.from(tempEl.rows).map((col) => col.getBoundingClientRect().height / stageScale),
        };
        if (!oldHeightInfo) return expectHeightInfo;
        if (
            oldHeightInfo.verticalSizes.every((size, index) =>
                isEqualNumber(size, expectHeightInfo.verticalSizes[index]),
            )
        ) {
            return oldHeightInfo;
        }
        return expectHeightInfo;
    };

    const getLayout: GetUpdatedTableStyle_ComputedHelp['getLayout'] = (_oldLayout) => {
        const oldLayout = _oldLayout as
            | {
                  tableWidth?: number;
                  horizontalSizes?: number[];
                  tableHeight?: number;
                  verticalSizes?: number[];
              }
            | undefined;
        console.time('===table元素 重排时间');
        const { width, height } = tempEl.getBoundingClientRect();
        console.timeEnd('===table元素 重排时间');
        const expectLayout: LayoutInfo = {
            tableWidth: width / stageScale,
            tableHeight: height / stageScale,
            baseCells: {
                horizontal: Array.from(tempEl.querySelectorAll('col')).map(
                    (col) => col.getBoundingClientRect().width / stageScale,
                ),
                vertical: Array.from(tempEl.rows).map((row) => row.getBoundingClientRect().height / stageScale),
            },
        };
        if (!oldLayout) return expectLayout;
        const wIsEqual = isEqualNumber(oldLayout.tableWidth!, expectLayout.tableWidth);
        const hIsEqual = isEqualNumber(oldLayout.tableHeight!, expectLayout.tableHeight);
        const hSizesIsEqual = oldLayout.horizontalSizes?.every((size, index) =>
            isEqualNumber(size, expectLayout.baseCells.horizontal[index]),
        );
        const vSizesIsEqual = oldLayout.verticalSizes?.every((size, index) =>
            isEqualNumber(size, expectLayout.baseCells.vertical[index]),
        );
        const noEqual = !wIsEqual && !hIsEqual && !hSizesIsEqual && !vSizesIsEqual;
        if (noEqual) return expectLayout;

        const newLayout: LayoutInfo = expectLayout;
        if (wIsEqual) {
            newLayout.tableWidth = oldLayout.tableWidth!;
        }
        if (hIsEqual) {
            newLayout.tableHeight = oldLayout.tableHeight!;
        }
        if (hSizesIsEqual) {
            newLayout.baseCells.horizontal = oldLayout.horizontalSizes!;
        }
        if (vSizesIsEqual) {
            newLayout.baseCells.vertical = oldLayout.verticalSizes!;
        }
        return newLayout;
    };

    const computedHelp: GetUpdatedTableStyle_ComputedHelp = {
        getHeightInfo,
        getLayout,
    };

    const getTempEl = (): HTMLTableElement => {
        let tempEl: HTMLTableElement | null = null;
        if (preserveTempElement) {
            tempEl = document.querySelector('#editingTempTable');
        }
        if (!tempEl) {
            document.querySelector('#editingTempTable')?.remove();
            tempEl = tableEl?.cloneNode(true) as HTMLTableElement;
            tempEl.style.visibility = 'hidden';
            tempEl.id = 'editingTempTable';
            tableEl?.parentElement!.appendChild(tempEl);
        }
        return tempEl;
    };
    const tempEl = getTempEl();
    // 需要旋转到水平方向上, 不然getBoundingClientRect拿到的不是宽高值
    tempEl.style.transform = `rotate(${-1 * tableRotate}deg)`;

    action(tempEl, actionHelp);

    const res = compute(tempEl, computedHelp);

    if (args.debug) {
        tempEl.style.visibility = 'initial';
        const debugStyleId = 'debugStyle';
        if (!tempEl.querySelector(`#${debugStyleId}`)) {
            const s = document.createElement('style');
            s.id = debugStyleId;
            s.innerHTML = `
                #editingTempTable td {
                    border-color: red !important;
                }
            `;
            tempEl.appendChild(s);
        }
    }

    if (!preserveTempElement) {
        tempEl.remove();
    }

    return res;
}

export interface MinSizeInfoByScale {
    width: number;
    height: number;
    /** COL_ELEMENT_IS_BLOCK为false时, 这个派上用场 */
    hMinSizes: (number | null)[];
}

/**
 * 获取下一帧缩放后的图层数据
 *
 * 缩放逻辑:
 * - 只缩放宽度时, 缩小宽度, 高度会自适应撑开
 * - 只缩放高度时, 缩小高度, 宽度不变
 * - 宽度和高度同时缩放时, 先走`只缩放宽`的逻辑, 再走`只缩放高`的逻辑
 */
export const getScaledTableLayer = ({
    frameworkIdentity,
    currentActiveTableLayer,
    originalBaseCells: { horizontal: originalHorizontalSizes, vertical: originalVerticalSizes },
    delta: { deltaOfWidth: expectDeltaOfWidth, deltaOfHeight: expectDeltaOfHeight },
    onLimit,
    minSizeInfo: minSizeInfoArg,
    shouldScaleAllAttr = false,
    onGetMinSizeInfo,
}: {
    frameworkIdentity: string;
    currentActiveTableLayer: ITableLayer;
    /** 最初的baseCells, 用于缩放过程中按照原始比例进行恢复 */
    originalBaseCells: BaseCells;
    delta: {
        deltaOfWidth?: number;
        deltaOfHeight?: number;
    };
    onLimit?: (direction: 'horizontal' | 'vertical') => void;
    /** 若没传minSizeInfo, 则会计算minSizeInfo并回调 (主要是为了性能优化, 防止一直计算, 这样minSizeInfo就只会计算一次了) */
    minSizeInfo?: MinSizeInfoByScale;
    /** 是否缩放所有属性(fontSize, borderWidth等) */
    shouldScaleAllAttr?: boolean;
    onGetMinSizeInfo?: (minSizeInfo: MinSizeInfoByScale) => void;
}): ITableLayer => {
    if (!expectDeltaOfHeight && !expectDeltaOfWidth) {
        return currentActiveTableLayer;
    }

    // 简单clone
    const newTableLayer: ITableLayer = {
        ...currentActiveTableLayer,
        data: {
            ...currentActiveTableLayer.data,
            baseCells: {
                horizontal: [...currentActiveTableLayer.data.baseCells.horizontal],
                vertical: [...currentActiveTableLayer.data.baseCells.vertical],
            },
        },
    };
    if (shouldScaleAllAttr && expectDeltaOfWidth) {
        const scale = (currentActiveTableLayer.width + expectDeltaOfWidth) / currentActiveTableLayer.width;
        for (const styleId in currentActiveTableLayer.data.styles) {
            const style = newTableLayer.data.styles[styleId];
            if (style.fontSize) {
                style.fontSize = Math.max(MIN_FONT_SIZE, Math.round(style.fontSize * scale));
            }
            if (style.border) {
                Object.values(style.border).forEach((borderItem) => {
                    borderItem.width = Math.max(MIN_BORDER_WIDTH, Math.round(borderItem.width * scale));
                });
            }
        }
    }
    const {
        baseCells: { horizontal: horizontalSizes, vertical: verticalSizes },
    } = newTableLayer.data;

    let minSizeInfo: MinSizeInfoByScale = {} as any;

    // 计算量太大了, 直接不降级处理
    const shouldPerformanceMinSizeInfo = horizontalSizes.length > 10 && verticalSizes.length > 10;

    // minSizeInfo
    if (minSizeInfoArg) {
        minSizeInfo = minSizeInfoArg;
    } else {
        minSizeInfo = getTableMinSize(newTableLayer);
        onGetMinSizeInfo?.(minSizeInfo);
    }

    const isOnlyChangeHeight = typeof expectDeltaOfHeight === 'number' && typeof expectDeltaOfWidth === 'undefined';
    const limitDirection =
        isOnlyChangeHeight && currentActiveTableLayer.height + expectDeltaOfHeight <= minSizeInfo.height
            ? 'vertical'
            : !isOnlyChangeHeight &&
                typeof expectDeltaOfWidth === 'number' &&
                currentActiveTableLayer.width + expectDeltaOfWidth <= minSizeInfo.width
              ? 'horizontal'
              : null;

    if (limitDirection) {
        onLimit?.(limitDirection);
    }

    const newHorizontalSizes = expectDeltaOfWidth
        ? getNewSizes({
              originalRatios: originalHorizontalSizes.map((w) => w / sum(originalHorizontalSizes)),
              currentSizes: horizontalSizes,
              deltaAllSize: expectDeltaOfWidth,
              minAllSize: minSizeInfo.width,
              minSizes:
                  COL_ELEMENT_IS_BLOCK || shouldPerformanceMinSizeInfo
                      ? undefined
                      : minSizeInfo.hMinSizes.map((size) => size ?? MIN_BASE_CELL_WIDTH),
          })
        : horizontalSizes;
    const newVerticalSizes = expectDeltaOfHeight
        ? getNewSizes({
              originalRatios: originalVerticalSizes.map((h) => h / sum(originalVerticalSizes)),
              currentSizes: verticalSizes,
              deltaAllSize: expectDeltaOfHeight,
              minAllSize: minSizeInfo.height,
          })
        : verticalSizes;

    const newInfo = getUpdatedTableStyle({
        frameworkIdentity: frameworkIdentity,
        tableLayerId: newTableLayer.id,
        tableRotate: newTableLayer.rotate,
        action: (el, { scaleFontSizeAndBorderWidth }) => {
            if (expectDeltaOfWidth) {
                // scale fontSize and borderWidth
                if (shouldScaleAllAttr && expectDeltaOfWidth) {
                    const scale = (currentActiveTableLayer.width + expectDeltaOfWidth) / currentActiveTableLayer.width;
                    scaleFontSizeAndBorderWidth({
                        currentTableLayerData: newTableLayer.data,
                        scale,
                    });
                }

                // 美图秀秀内嵌(qtWebEngine)用max-content, 会导致文字内容无法撑开单元格(即文字被单元格截断), 所以得用initail
                el.style.width = expectDeltaOfWidth >= 0 ? 'max-content' : 'initial';
                el.querySelectorAll('col').forEach((colEl, index) => {
                    colEl.style.width = newHorizontalSizes[index] + 'px';
                });
            }
            if (expectDeltaOfHeight) {
                Array.from(el.rows).forEach((trEl, index) => {
                    const newHeight = newVerticalSizes[index];
                    trEl.style.height = newHeight + 'px';
                });
                const style = document.createElement('style');
                style.innerText = `td { height: ${MIN_BASE_CELL_HEIGHT}px !important; }`;
                el.appendChild(style);
            }
        },
        compute: (_, { getLayout }) => getLayout(),
    })!;

    if (COL_ELEMENT_IS_BLOCK) {
        newTableLayer.width = newInfo.tableWidth;
        newTableLayer.height = newInfo.tableHeight;
        newTableLayer.data.baseCells = newInfo.baseCells;
    } else {
        // 降级处理
        if (expectDeltaOfWidth) {
            newTableLayer.data.baseCells.horizontal = horizontalSizes.map((_, index) => newHorizontalSizes[index]);
            newTableLayer.width += sum(newTableLayer.data.baseCells.horizontal) - sum(horizontalSizes);
        }
        newTableLayer.height = newInfo.tableHeight;
        newTableLayer.data.baseCells.vertical = newInfo.baseCells.vertical;
    }

    return newTableLayer;

    type FixHSizesInfo = { tdElId: string | null; hIndex: number };
    /** COL_ELEMENT_IS_BLOCK为false的情况下，指定某个cell的width去修复horizontalSizes (只能尽量去修复hSizes, 如果全是合并单元格, 找不到能修复的cell的话, 就不管了) */
    function getFixHSizesInfoList(layer: ITableLayer): FixHSizesInfo[] {
        const cellsProp = layer.data.cells;
        const baseColCount = layer.data.baseCells.horizontal.length;

        if (shouldPerformanceMinSizeInfo) return [];

        const getTdElId = (hIndex: number): string | null => {
            const targetCells = cellsProp
                .flat()
                .filter((c) => c && c.content && c.hIndex === hIndex && c.colSpan === 1) as Cell[];
            if (!targetCells.length) return null;
            const cell = maxBy(targetCells, (c) => getCellStyle(c, layer.data).fontSize);
            if (!cell) return null;
            return getTdElementId({
                id: cell.__renderId,
                hIndex: cell.hIndex,
                vIndex: cell.vIndex,
            });
        };

        return range(0, baseColCount).map((hIndex) => ({
            tdElId: getTdElId(hIndex),
            hIndex,
        }));
    }

    /** 返回的width是最小的宽度情况, 返回的height是最小的高度情况 */
    function getTableMinSize(layer: ITableLayer): MinSizeInfoByScale {
        const minSizeInfo = {} as MinSizeInfoByScale;
        getUpdatedTableStyle({
            frameworkIdentity,
            tableLayerId: layer.id,
            tableRotate: layer.rotate,
            action: (el, { scaleFontSizeAndBorderWidth }) => {
                if (shouldScaleAllAttr && expectDeltaOfWidth) {
                    const scale = (currentActiveTableLayer.width + expectDeltaOfWidth) / currentActiveTableLayer.width;
                    scaleFontSizeAndBorderWidth({
                        currentTableLayerData: newTableLayer.data,
                        scale,
                    });
                }

                const style = document.createElement('style');
                style.innerHTML = `
                    #${el.id} {
                        width: initial !important;
                    }
                    #${el.id} col {
                        width: ${MIN_BASE_CELL_WIDTH}px !important;
                    }
                `;
                el.appendChild(style);
                const stageScale = getFramework(frameworkIdentity).stage.getScale();
                console.time('===getTableMinSize-table元素 重排时间1');
                minSizeInfo.width = el.getBoundingClientRect().width / stageScale;
                console.timeEnd('===getTableMinSize-table元素 重排时间1');
                if (!COL_ELEMENT_IS_BLOCK) {
                    // 限制最小宽度
                    const fixHSizesInfoList = getFixHSizesInfoList(layer);
                    minSizeInfo.hMinSizes = fixHSizesInfoList.map(({ tdElId }) => {
                        if (tdElId) {
                            const tdEl = el.querySelector(`#${tdElId}`);
                            return tdEl ? tdEl.getBoundingClientRect().width / stageScale : null;
                        }
                        return null;
                    });
                }
                style.innerHTML = `
                    #${el.id} {
                        width: max-content !important;
                        table-layout: auto !important;
                    }
                    #${el.id} col {
                        width: initial !important;
                    }
                    #${el.id} tr, #${el.id} td {
                        height: ${MIN_BASE_CELL_HEIGHT}px !important;
                    }
                `;
                console.time('===getTableMinSize-table元素 重排时间2');
                minSizeInfo.height = el.getBoundingClientRect().height / stageScale;
                console.timeEnd('===getTableMinSize-table元素 重排时间2');
                style.remove();
            },
            compute: noop,
        });

        return minSizeInfo;
    }

    /** 缩放后的sizes, 缩小时按等比例缩小, 放大时按等原始比例放大(目的是恢复原样) */
    function getNewSizes({
        originalRatios,
        currentSizes,
        deltaAllSize,
        minAllSize,
        minSizes,
    }: {
        originalRatios: number[];
        currentSizes: number[];
        deltaAllSize: number;
        minAllSize: number;
        minSizes?: number[];
    }) {
        if (deltaAllSize <= 0) {
            const minDeltaSize = minAllSize - sum(currentSizes);
            const resultDeltaAllSize = Math.max(deltaAllSize, minDeltaSize);
            const expectSizes = currentSizes.map((size) => size + resultDeltaAllSize / currentSizes.length);
            // 用minSizes限制最小宽度 (解决COL_ELEMENT_IS_BLOCK为false时的bug: https://jira.meitu.com/browse/MTXXPRO-54382)
            if (minSizes?.some(Boolean)) {
                const getExpectSizes = (expectSizes: number[]): number[] => {
                    const exceedInfoList = expectSizes.map((size, index) => ({
                        index,
                        minSize: minSizes[index],
                        isExceed: minSizes[index] > size,
                        exceedSize: minSizes[index] > size ? minSizes[index] - size : 0,
                        originalExpectSize: size,
                    }));
                    if (!exceedInfoList.some((info) => info.isExceed)) return expectSizes;

                    const exceedList = exceedInfoList.filter((info) => info.isExceed);
                    const noExceedList = exceedInfoList.filter((info) => !info.isExceed);
                    const restDeltaSize = sum(exceedList.map((info) => info.exceedSize));
                    const averageDeltaSize = restDeltaSize / noExceedList.length;
                    const newExpectSizes = exceedInfoList.map((info) => {
                        if (info.isExceed) return info.minSize;
                        return info.originalExpectSize + averageDeltaSize;
                    });
                    return newExpectSizes;
                };
                return getExpectSizes(expectSizes);
            }
            return expectSizes;
        }
        // TODO：完美还原的逻辑: 等同于数学题: 已知当前数组为[a, b, c, d], 有个值x, 要求数组的每个元素去随意分配x, 并自增这个分配后的值, 要求自增后的新数组的比例趋近于已知比例f:g:h:i, 求新数组
        // 下面的代码有个问题: 缩放到极限值时, 再放大比例会丢失. 既然完美还原逻辑实现不出来，那就先这样处理了
        const minDeltaSize = minAllSize - sum(currentSizes);
        const resultDeltaAllSize = Math.max(deltaAllSize, minDeltaSize);
        const expectSizes = currentSizes.map((size) => size + resultDeltaAllSize / currentSizes.length);
        return expectSizes;
    }
};

export const getCellStyle = (
    cell: Cell,
    tableLayerData: Pick<ITableLayerProps, 'allStyleId' | 'styles' | 'presetId'>,
): CellStyle => {
    const defaultStyle =
        tablePresets.find((p) => p.id === tableLayerData.presetId)?.getCellDefaultStyle?.(cell, tableLayerData) ??
        tableLayerData.styles[tableLayerData.allStyleId];
    const cellStyle = cell.styleIds.reduce<CellStyle>((res, styleId) => {
        return {
            ...res,
            ...tableLayerData.styles[styleId],
        };
    }, {});
    return {
        ...defaultStyle,
        ...cellStyle,
    };
};

export const getActiveTableLayer = (frameworkIdentity: string): ITableLayer | null => {
    const framework = getFramework(frameworkIdentity);
    const activePickLayer = framework.operate.getActivePickLayer();
    if (activePickLayer?.layerType === LayerType.TABLE) return activePickLayer as ITableLayer;
    return null;
};

type FontItem = { fontFamily: string; text: string };

export const collectTableLayerFonts = (tableLayerData: ITableLayerProps | ITableLayerProps<true>): FontItem[] => {
    const fontList = (tableLayerData.cells.flat().filter(Boolean) as Cell[])
        .map<FontItem>((c) => {
            const style = getCellStyle(c, tableLayerData);
            return {
                fontFamily: style.fontFamily ?? DEFAULT_FONT_FAMILY().name,
                text: c.content,
            };
        })
        // 重复的fontFamily进行合并
        .reduce<FontItem[]>((fontList, font) => {
            const targetFont = fontList.find((f) => f.fontFamily === font.fontFamily);
            if (targetFont) targetFont.text += font.text;
            else fontList.push(font);
            return fontList;
        }, []);
    return fontList.filter((f) => f.text);
};

export const getFixedHsizes = (layer: ITableLayer): number[] => {
    const leftBorderWidth = getCellStyle(layer.data.cells[0][0]!, layer.data).border?.left?.width ?? 0;
    const rightAnyCell = (layer.data.cells.flat().filter(Boolean) as Cell[]).find(
        (c) => c.hIndex + c.colSpan === layer.data.baseCells.horizontal.length,
    )!;
    const rightBorderWidth = getCellStyle(rightAnyCell, layer.data).border?.right?.width ?? 0;
    const innerWidth = layer.width - leftBorderWidth / 2 - rightBorderWidth / 2;
    const colNumSum = sum(layer.data.baseCells.horizontal);
    const ratio = innerWidth / colNumSum;
    return layer.data.baseCells.horizontal.map((size) => size * ratio);
};
